import React from 'react';
import { Eye, Layers } from 'lucide-react';
import { FormField } from '../../../types/formBuilder';
import { FormFieldRenderer } from '../preview/FormFieldRenderer';

interface FieldPreviewProps {
  field: FormField;
}

export const FieldPreview: React.FC<FieldPreviewProps> = ({ field }) => {
  if (!field.type) {
    return (
      <div className="p-6 text-center text-th-secondary">
        <div className="w-12 h-12 bg-elevated rounded-lg flex items-center justify-center mx-auto mb-3">
          <Eye className="w-6 h-6" />
        </div>
        <p className="text-sm">Select a field type to see preview</p>
      </div>
    );
  }

  if (field.type === 'prompt_builder') {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center space-x-2 mb-4">
          <Layers className="w-5 h-5 text-brand" />
          <h4 className="text-md font-semibold text-th-primary">Prompt Builder Preview</h4>
        </div>
        
        {field.promptBuilderConfig?.template ? (
          <div className="space-y-4">
            <div className="p-4 bg-brand/10 border border-brand/20 rounded-lg">
              <h5 className="text-brand font-medium text-sm mb-2">Template:</h5>
              <p className="text-th-primary text-sm font-mono">
                {field.promptBuilderConfig.template}
              </p>
            </div>
            
            {field.promptBuilderConfig.sub_fields.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-th-primary font-medium text-sm">Sub-Fields:</h5>
                {field.promptBuilderConfig.sub_fields.map((subField, index) => (
                  <div key={index} className="p-3 bg-elevated border border-subtle rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-th-primary font-medium text-sm">{subField.label}</span>
                      <span className="text-xs text-accent bg-accent/20 px-2 py-0.5 rounded">
                        {subField.type}
                      </span>
                    </div>
                    <FormFieldRenderer
                      field={subField}
                      value={subField.defaultValue || ''}
                      hasError={false}
                      disabled={true}
                      onChange={() => {}} // Preview only, no interaction
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-th-secondary">
            <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Configure template and sub-fields to see preview</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center space-x-2 mb-4">
        <Eye className="w-5 h-5 text-accent" />
        <h4 className="text-md font-semibold text-th-primary">Field Preview</h4>
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium text-th-primary">
          {field.label || 'Field Label'}
          {field.required && <span className="text-status-error ml-1">*</span>}
          {field.advanced && (
            <span className="ml-2 text-xs text-status-warning bg-status-warning/20 px-2 py-0.5 rounded">
              Advanced
            </span>
          )}
        </label>
        
        {field.description && (
          <p className="text-sm text-th-secondary">{field.description}</p>
        )}
        
        <FormFieldRenderer
          field={field}
          value={field.defaultValue || ''}
          hasError={false}
          disabled={true}
          onChange={() => {}} // Preview only, no interaction
        />
      </div>
    </div>
  );
};